package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr

/**
 * Estrutura do Word: Modelo Relatório Fotográfico-Alojamentos.
 * Subitens numerados 1.1, 1.2…; seção 8 = Observações Gerais (texto + fotos, sem C/NC/NA).
 * Itens ativos/títulos vêm de [AppConfig].
 */
object RelatorioFotoCatalog {

    const val SECAO_OBSERVACOES = "8. Observações Gerais"

    private var configAtual: AppConfig? = null

    fun recarregar(config: AppConfig) {
        configAtual = config
    }

    fun itensBase(): List<ItemConformidade> = secoesBase().flatMap { it.itens }

    fun secoesBase(): List<SecaoConformidade> = listOf(
        secao(
            "1. Visão Geral do Alojamento",
            listOf(
                item("1.1", "Fachada externa do alojamento"),
                item("1.2", "Áreas comuns (corredores, circulação) — vista 1"),
                item("1.3", "Áreas comuns (corredores, circulação) — vista 2"),
                item("1.4", "Áreas comuns (corredores, circulação) — vista 3")
            )
        ),
        secao(
            "2. Dormitórios",
            listOf(
                item("2.1", "Visão geral dos dormitórios"),
                item("2.2", "Camas (mostrar disposição e espaçamento)"),
                item(
                    "2.3",
                    "Espaçamento entre as camas",
                    tipoExtra = TipoCampoExtra.NUMERO,
                    labelExtra = "Espaçamento (m)"
                ),
                item("2.4", "Colchões"),
                item(
                    "2.5",
                    "Quantos travesseiros e forro de cama (por cama)",
                    tipoExtra = TipoCampoExtra.NUMERO,
                    labelExtra = "Quantidade por cama"
                ),
                item("2.6", "Armários individuais"),
                item("2.7", "Ventilação"),
                item("2.8", "Iluminação")
            )
        ),
        secao(
            "3. Banheiros e Vestiários",
            listOf(
                item("3.1", "Visão geral do banheiro/vestiário"),
                item("3.2", "Vasos sanitários"),
                item("3.3", "Chuveiros"),
                item("3.4", "Água quente disponível?"),
                item("3.5", "Lavatórios (pia)"),
                item("3.6", "Lixeira"),
                item("3.7", "Suporte de papel")
            )
        ),
        secao(
            "4. Água Potável",
            listOf(
                item("4.1", "Bebedouros"),
                item("4.2", "Identificação de água potável"),
                item("4.3", "Condições de limpeza")
            )
        ),
        secao(
            "5. Cozinha / Refeitório",
            listOf(
                item("5.1", "Visão geral da cozinha/refeitório"),
                item("5.2", "Pia e torneiras"),
                item("5.3", "Fogão / geladeira"),
                item("5.4", "Mesas e assentos"),
                item("5.5", "Botijão de gás"),
                item("5.6", "Lixeiras com tampa")
            )
        ),
        secao(
            "6. Limpeza e Higienização",
            listOf(
                item("6.1", "Visão geral da lavanderia"),
                item("6.2", "Local de guarda de materiais de limpeza"),
                item("6.3", "Tanquinho/centrifuga"),
                item("6.4", "Cronograma de limpeza"),
                item("6.5", "Varal")
            )
        ),
        secao(
            "7. Segurança",
            listOf(
                item("7.1", "Extintores de incêndio (com validade visível)"),
                item("7.2", "Sinalização de emergência"),
                item("7.3", "Rotas de fuga"),
                item("7.4", "Iluminação de emergência")
            )
        )
        // Seção 8 = Observações Gerais: texto + fotos no FormRelatorioFoto (sem itens C/NC/NA).
    )

    fun secoesPadrao(): List<SecaoConformidade> {
        val ativos = itensPadrao()
        return secoesBase().mapNotNull { secao ->
            val itens = ativos.filter { it.secao == secao.titulo }
            if (itens.isEmpty()) null else secao.copy(itens = itens)
        }
    }

    fun itensPadrao(): List<ItemConformidade> {
        val byId = configAtual?.camposAlojamentoFoto?.associateBy { it.id }.orEmpty()
        return itensBase().mapNotNull { base ->
            val cfg = byId[base.id]
            when {
                byId.isEmpty() || cfg == null -> base
                !cfg.ativo -> null
                else -> base.copy(
                    titulo = cfg.titulo.ifBlank { base.titulo },
                    obrigatorio = cfg.obrigatorio,
                    fotoObrigatoria = cfg.fotoObrigatoriaSeSim,
                    fotoHabilitada = true
                )
            }
        }
    }

    private fun secao(titulo: String, itens: List<ItemConformidade>) =
        SecaoConformidade(
            titulo = titulo,
            itens = itens.map { it.copy(secao = titulo) }
        )

    private fun item(
        numero: String,
        titulo: String,
        tipoExtra: TipoCampoExtra = TipoCampoExtra.NENHUM,
        labelExtra: String = "",
        fotoObrigatoria: Boolean = true,
        obrigatorio: Boolean = true
    ) = ItemConformidade(
        id = "rf_$numero",
        numero = numero,
        titulo = titulo,
        secao = "",
        tipoExtra = tipoExtra,
        labelExtra = labelExtra,
        fotoObrigatoria = fotoObrigatoria,
        obrigatorio = obrigatorio
    )
}

data class FormRelatorioFoto(
    val responsavelAlojamento: String = "",
    val endereco: String = "",
    val dataInicioContrato: String = "",
    val obra: String = "",
    val capacidadeTotalPessoas: String = "",
    val quantidadeFuncionarios: String = "",
    val quantidadeQuartos: String = "",
    val quantidadeCamas: String = "",
    val quantidadeArmarios: String = "",
    val quantidadeBanheiros: String = "",
    val dataHorarioMs: Long = currentTimeMillis(),
    val latitude: Double? = null,
    val longitude: Double? = null,
    val localTexto: String = "",
    val itens: List<ItemConformidade> = RelatorioFotoCatalog.itensPadrao(),
    val observacoesGerais: String = "",
    val observacoesFotos: List<FotoAnexo> = emptyList()
) {
    fun dataHorarioFormatado(): String = formatDateBr(dataHorarioMs)

    fun fotosParaExportacao(): List<Pair<String, FotoAnexo>> =
        fotosDetalhadasParaExportacao().map { it.secao to it.foto }

    fun fotosDetalhadasParaExportacao(): List<FotoExportItem> = buildList {
            itens.forEach { item ->
                val resp = item.resposta?.rotulo ?: "—"
                item.fotos.forEachIndexed { index, foto ->
                    add(
                        FotoExportItem(
                            secao = "${item.tituloNumerado} — Foto ${index + 1}",
                            resposta = resp,
                            foto = foto
                        )
                    )
                }
            }
            observacoesFotos.forEachIndexed { index, foto ->
                add(
                    FotoExportItem(
                        secao = "${RelatorioFotoCatalog.SECAO_OBSERVACOES} — Foto ${index + 1}",
                        resposta = "—",
                        foto = foto
                    )
                )
            }
        }
}

fun FormRelatorioFoto.semConteudoBase64(): FormRelatorioFoto = copy(
    itens = itens.map { it.copy(fotos = it.fotos.map { f -> f.copy(conteudoBase64 = null) }) },
    observacoesFotos = observacoesFotos.map { it.copy(conteudoBase64 = null) }
)

fun FormRelatorioFoto.temConteudoRascunho(): Boolean =
    responsavelAlojamento.isNotBlank() ||
        endereco.isNotBlank() ||
        obra.isNotBlank() ||
        itens.any { it.resposta != null || it.fotos.isNotEmpty() || it.justificativa.isNotBlank() } ||
        observacoesGerais.isNotBlank() ||
        observacoesFotos.isNotEmpty()
